import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { categories, articles, siteConfig } from '@/mocks/data';

const catArticleImages: Record<string, string> = {
  'best-free-movie-sites-2025': 'https://readdy.ai/api/search-image?query=Movie%20and%20film%20entertainment%20collage%20with%20warm%20lighting%20and%20cinematic%20atmosphere%2C%20film%20reels%20and%20posters%20on%20dark%20wood%20surface%20with%20soft%20golden%20glow%2C%20elegant%20home%20theater%20aesthetic&width=800&height=450&seq=cat-art-01&orientation=landscape',
  'how-to-choose-streaming-site': 'https://readdy.ai/api/search-image?query=Cozy%20living%20room%20with%20home%20theater%20setup%20and%20movie%20posters%20on%20wall%2C%20warm%20evening%20ambient%20light%2C%20comfortable%20seating%20with%20cushions%2C%20cinematic%20atmosphere&width=800&height=450&seq=cat-art-02&orientation=landscape',
  'top-korean-drama-sites': 'https://readdy.ai/api/search-image?query=Korean%20drama%20romantic%20scene%20with%20warm%20autumn%20lighting%2C%20couple%20walking%20under%20golden%20ginkgo%20trees%2C%20soft%20focus%20cinematic%20look%2C%20emotional%20atmosphere&width=800&height=450&seq=cat-art-03&orientation=landscape',
  'anime-streaming-guide': 'https://readdy.ai/api/search-image?query=Colorful%20anime%20style%20artwork%20with%20vibrant%20characters%20and%20dreamy%20sky%20background%2C%20Japanese%20animation%20aesthetic%2C%20warm%20sunset%20colors%2C%20detailed%20illustration&width=800&height=450&seq=cat-art-04&orientation=landscape',
  'documentary-must-watch': 'https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20mountain%20range%20at%20sunrise%20with%20golden%20light%2C%20pristine%20nature%20landscape%2C%20documentary%20cinematography%20style%2C%20dramatic%20clouds&width=800&height=450&seq=cat-art-05&orientation=landscape',
  'variety-show-2025': 'https://readdy.ai/api/search-image?query=Colorful%20TV%20show%20stage%20with%20dynamic%20lighting%20and%20neon%20accents%2C%20entertainment%20studio%20set%2C%20lively%20performance%20atmosphere%2C%20warm%20spotlight%20glow&width=800&height=450&seq=cat-art-06&orientation=landscape',
  'mobile-movie-watching-tips': 'https://readdy.ai/api/search-image?query=Smartphone%20on%20bedside%20table%20playing%20movie%20with%20headphones%2C%20cozy%20bedroom%20atmosphere%2C%20warm%20nighttime%20lighting%2C%20comfortable%20evening%20vibe&width=800&height=450&seq=cat-art-07&orientation=landscape',
  'classic-movie-collection': 'https://readdy.ai/api/search-image?query=Vintage%20cinema%20decor%20with%20classic%20film%20posters%20and%20antique%20projector%2C%20nostalgic%20warm%20lighting%2C%20old%20Hollywood%20glamour%20aesthetic%2C%20rich%20wood%20textures&width=800&height=450&seq=cat-art-08&orientation=landscape',
  'free-vs-vip-comparison': 'https://readdy.ai/api/search-image?query=Comparison%20of%20premium%20cinema%20experience%20versus%20cozy%20home%20viewing%2C%20split%20screen%20aesthetic%2C%20warm%20ambient%20lighting%20on%20both%20sides%2C%20balanced%20composition&width=800&height=450&seq=cat-art-09&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.id === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-3">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);
  const otherCategories = categories.filter((c) => c.id !== slug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteConfig.name}`,
    description: category.description,
    url: `${siteConfig.url}/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${category.name}推荐`}
        description={category.description}
        keywords={`${category.name},看片网站,免费看片网站推荐,在线看片网站,好用的看片网站`}
        canonicalUrl={`${siteConfig.url}/category/${slug}`}
        schema={categorySchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-500 flex items-center justify-center">
                  <i className={`${category.icon} text-2xl md:text-3xl text-background-50`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    {category.name}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-500 mt-1">
                    {category.description}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-4 md:py-6 bg-background-50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                <span className="text-sm text-foreground-500 mr-2 whitespace-nowrap">切换分类：</span>
                {otherCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="px-4 py-1.5 rounded-full text-sm border border-background-300/60 text-foreground-600 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-48 md:h-52 overflow-hidden">
                        <img
                          src={catArticleImages[article.id]}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2 flex-wrap">
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i> {article.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i> {article.readTime}
                          </span>
                          <span className="flex items-center gap-1 text-accent-600">
                            <i className="ri-star-fill"></i> {article.rating}
                          </span>
                        </div>
                        <h2 className="text-base md:text-lg font-semibold text-foreground-900 font-heading mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类下暂无文章，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}